/**
 * WindowedRowCache — sparse-cached, LRU-evicting page loader for huge lists.
 *
 * Extracted from `useWindowedRows` (see that file's doc comment for the full
 * rationale) so tree grouping (one cache per expanded group) can instantiate
 * this per-group instead of relying on a hook, which can't be created
 * dynamically. This class is transport-agnostic: it only knows `fetchPage`.
 *
 * Strategy (unchanged from the hook):
 *   - Rows are fetched in chunks of `chunkSize` (= 1 server page).
 *   - Loaded chunks sit in a Map<pageNumber, Row[]>.
 *   - Each chunk has a touched-at counter; when the cache grows past
 *     `maxPagesInMemory`, the least-recently-touched chunks are evicted.
 *   - `getRow(absoluteIndex)` returns:
 *       { state: 'loaded',  row }  — chunk is in cache, touched.
 *       { state: 'loading'      }  — chunk is being fetched.
 *       { state: 'placeholder'  }  — index outside known total (shouldn't happen).
 *   - Probing an unloaded chunk schedules a fetch as a side effect (idempotent).
 *   - The first page is fetched immediately on construction (mirrors what the
 *     hook's mount effect used to do) so `loadingInitial` — not `loadingMore`
 *     — is true while it is in flight.
 */

export interface WindowedFetchResult {
  rows: Record<string, any>[]
  total: number
}

export type WindowedFetcher = (page: number, chunkSize: number) => Promise<WindowedFetchResult>

export type RowState =
  | { state: 'loaded'; row: Record<string, any> }
  | { state: 'loading' }
  | { state: 'placeholder' }

export interface WindowedRowCacheOptions {
  fetchPage: WindowedFetcher
  /** Server page size. Smaller = more requests + less memory per chunk. */
  chunkSize?: number
  /** LRU cap — number of chunks held in memory at once. */
  maxPagesInMemory?: number
  /** Called after any state change (new data, error, loading flags) so the caller can re-render. */
  onChange: () => void
}

type FetchMode = 'replace' | 'append'

export class WindowedRowCache {
  private readonly fetchPageFn: WindowedFetcher
  private readonly chunkSize: number
  private readonly maxPagesInMemory: number
  private readonly onChange: () => void

  private cache = new Map<number, Record<string, any>[]>()
  private touched = new Map<number, number>()
  private inFlight = new Set<number>()
  private requestId = 0
  private touchCounter = 0
  private disposed = false

  total = 0
  loadingInitial = false
  loadingMore = false
  error: string | null = null

  constructor(opts: WindowedRowCacheOptions) {
    this.fetchPageFn = opts.fetchPage
    this.chunkSize = opts.chunkSize ?? 50
    this.maxPagesInMemory = opts.maxPagesInMemory ?? 20
    this.onChange = opts.onChange
    // Prime the first chunk immediately (mirrors the hook's old mount effect):
    // this is what keeps `loadingInitial` (not `loadingMore`) true for page 1.
    this.fetchPage(1, 'replace')
  }

  private evictLru() {
    if (this.cache.size <= this.maxPagesInMemory) return
    const sorted = Array.from(this.touched.entries()).sort((a, b) => a[1] - b[1])
    while (this.cache.size > this.maxPagesInMemory && sorted.length) {
      const [pageNum] = sorted.shift()!
      this.cache.delete(pageNum)
      this.touched.delete(pageNum)
    }
  }

  private async fetchPage(pageNum: number, mode: FetchMode) {
    if (this.disposed) return
    if (this.inFlight.has(pageNum)) return
    this.inFlight.add(pageNum)
    const myRequestId = this.requestId
    if (mode === 'replace') this.loadingInitial = true
    else this.loadingMore = true
    this.onChange()
    try {
      const result = await this.fetchPageFn(pageNum, this.chunkSize)
      if (this.disposed) return
      if (myRequestId !== this.requestId) return // stale
      this.total = result.total
      this.cache.set(pageNum, result.rows)
      this.touched.set(pageNum, ++this.touchCounter)
      this.evictLru()
      this.error = null
      this.onChange()
    } catch (e: any) {
      if (this.disposed) return
      if (myRequestId === this.requestId) {
        this.error = e?.message || 'Failed to load data'
        this.onChange()
      }
    } finally {
      if (this.disposed) return
      this.inFlight.delete(pageNum)
      if (myRequestId === this.requestId) {
        if (mode === 'replace') this.loadingInitial = false
        if (this.inFlight.size === 0) this.loadingMore = false
        this.onChange()
      }
    }
  }

  /** Reset the cache and reload from page 1. */
  reload(): void {
    if (this.disposed) return
    this.requestId += 1
    this.cache.clear()
    this.touched.clear()
    this.inFlight.clear()
    this.total = 0
    this.error = null
    this.onChange()
    this.fetchPage(1, 'replace')
  }

  /** Look up a row by absolute index. Triggers chunk fetch as a side effect. */
  getRow(index: number): RowState {
    const total = this.total
    if (total > 0 && index >= total) return { state: 'placeholder' }
    const size = this.chunkSize
    const pageNum = Math.floor(index / size) + 1
    const offset = index % size
    const page = this.cache.get(pageNum)
    if (page) {
      this.touched.set(pageNum, ++this.touchCounter)
      const row = page[offset]
      if (row) return { state: 'loaded', row }
      return { state: 'placeholder' }
    }
    // Schedule an idempotent fetch for the missing chunk.
    if (!this.inFlight.has(pageNum) && !this.disposed) {
      Promise.resolve().then(() => this.fetchPage(pageNum, 'append'))
    }
    return { state: 'loading' }
  }

  /** Patch a single row in-place (used after PATCH/PUT to avoid a full reload). */
  patchRow(recordId: string, updater: (row: Record<string, any>) => Record<string, any>): void {
    if (this.disposed) return
    const id = String(recordId)
    let changed = false
    this.cache.forEach((rows, pageNum) => {
      const idx = rows.findIndex((r) => String(r.id) === id)
      if (idx < 0) return
      const next = [...rows]
      next[idx] = updater(rows[idx])
      this.cache.set(pageNum, next)
      changed = true
    })
    if (changed) this.onChange()
  }

  /** Remove a row by id from the cache (used after delete). */
  removeRow(recordId: string): void {
    if (this.disposed) return
    const id = String(recordId)
    let changed = false
    this.cache.forEach((rows, pageNum) => {
      if (!rows.some((r) => String(r.id) === id)) return
      this.cache.set(pageNum, rows.filter((r) => String(r.id) !== id))
      changed = true
    })
    if (changed) {
      if (this.total > 0) this.total -= 1
      this.onChange()
    }
  }

  /**
   * Cancels in-flight fetch completions and makes all mutators (`patchRow`,
   * `removeRow`, `reload`, `getRow`'s fetch-scheduling side effect) no-ops.
   * Does NOT clear `cache`/`total`/`error` — a disposed cache is expected to
   * keep serving its last-known-settled state to callers that still hold a
   * reference (see `useWindowedRows`'s enabled→false transition, which keeps
   * the disposed instance around for reads). `loadingInitial`/`loadingMore`
   * are forced to false here since a disposed cache can never resolve them.
   */
  dispose(): void {
    this.disposed = true
    this.loadingInitial = false
    this.loadingMore = false
  }
}
